import { $, $$ } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { api } from '../lib/api.js';
import { trackDirtyForm } from '../lib/dirty-form.js';
import { toastError, toastSuccess } from '../lib/toast.js';
import { readText, readBoolean } from '../lib/form.js';
import { findSelectMenu, upgradeSelects } from '../lib/select-menu.js';

/**
 * The Economy page.
 *
 * The dials save in one request; each shop item saves in its own, so a single
 * bad item cannot lose the four beside it — the complaint this repository
 * already answered on the Welcome page.
 */
export function initEconomyPage(): void {
  const found = $<HTMLFormElement>('[data-economy-form]');
  if (!found) return;

  const form: HTMLFormElement = found;
  const guildId = form.dataset.guildId ?? '';

  wireList(form);
  wirePanel(form, guildId);

  trackDirtyForm({
    form,
    successMessage: ct('settingsSaved', 'Settings saved.'),
    onSave: async () => {
      await api.put(`/api/economy?guildId=${guildId}`, {
        guildId,
        currencyName: readText(form, 'currencyName') || 'pièces',
        currencySymbol: readText(form, 'currencySymbol') || '🪙',
        dailyAmount: number(form, 'dailyAmount', 100),
        messageAmount: number(form, 'messageAmount', 1),
        messageCooldownSeconds: seconds(readText(form, 'messageCooldown')),
        minTenureSeconds: seconds(readText(form, 'minTenure')),
        coinflipEnabled: readBoolean(form, 'coinflipEnabled'),
        coinflipMaxBet: number(form, 'coinflipMaxBet', 500),
      });

      for (const row of rows(form)) {
        const payload = readItem(row, guildId);
        if (!payload.name || !payload.roleId) continue;

        const id = row.dataset.itemId;

        try {
          const answer = await (id
            ? api.put<{ item: { _id: string } }>(
                `/api/economy/items/${id}?guildId=${guildId}`,
                payload,
              )
            : api.post<{ item: { _id: string } }>(
                `/api/economy/items?guildId=${guildId}`,
                payload,
              ));

          // An item created here gains its identifier, so the next save updates
          // it rather than making a second copy.
          row.dataset.itemId = answer.item._id;
        } catch (error) {
          toastError(`${payload.name}: ${error instanceof Error ? error.message : String(error)}`);
        }
      }
    },
  });
}

/**
 * The "publish the panel" button.
 *
 * Takes the channel as the picker stands, without saving first: whoever has
 * just chosen a channel expects the button under it to use that one.
 */
function wirePanel(form: HTMLFormElement, guildId: string): void {
  const publish = document.querySelector<HTMLButtonElement>('[data-publish-economy-panel]');
  const picker = document.querySelector<HTMLSelectElement>('[data-field="panelChannelId"]');

  publish?.addEventListener('click', () => {
    const channelId = findSelectMenu(form, 'panelChannelId')?.single ?? picker?.value ?? '';

    if (!channelId) {
      toastError(new Error(ct('chooseChannel', 'Choose a channel first.')));
      return;
    }

    void api
      .post(`/api/economy/panel?guildId=${guildId}`, { guildId, channelId })
      .then(() => {
        toastSuccess(ct('controllerPublished', 'Panel published.'));
      })
      .catch((error: unknown) => {
        toastError(error);
      });
  });
}

function readItem(row: HTMLElement, guildId: string) {
  return {
    guildId,
    name: text(row, 'name'),
    description: text(row, 'description'),
    price: Number(text(row, 'price')) || 1,
    roleId: findSelectMenu(row, 'roleId')?.single ?? '',
    requiredRoleIds: findSelectMenu(row, 'requiredRoleIds')?.value ?? [],
    stock: Number(text(row, 'stock') || '-1'),
    enabled: checked(row, 'enabled'),
  };
}

/* ------------------------------------------------------------------ the rows */

function wireList(form: HTMLFormElement): void {
  $('[data-add="item"]', form)?.addEventListener('click', () => {
    addRow(form);
  });

  $<HTMLElement>('[data-list="item"]', form)?.addEventListener('click', (event) => {
    const button = (event.target as HTMLElement).closest('[data-remove]');
    if (!button) return;

    const row = button.closest<HTMLElement>('[data-rule-row]');
    const id = row?.dataset.itemId;

    // A stored item goes at once: leaving it to the save would mean removing a
    // row and closing the page quietly keeps it on sale.
    if (id) {
      void api
        .delete(`/api/economy/items/${id}?guildId=${form.dataset.guildId ?? ''}`)
        .catch(() => toastError(ct('shopItemDeleteFailed', 'The item could not be removed.')));
    }

    row?.remove();
    refreshEmptyState(form);
    form.dispatchEvent(new Event('change', { bubbles: true }));
  });

  refreshEmptyState(form);
}

function addRow(form: HTMLFormElement): void {
  const template = $<HTMLTemplateElement>('[data-template="item"]');
  const list = $<HTMLElement>('[data-list="item"]', form);
  if (!template || !list) return;

  const holder = document.createElement('div');
  holder.innerHTML = template.innerHTML;

  const row = holder.querySelector('[data-rule-row]');
  if (!row) return;

  list.append(row);
  upgradeSelects(document);

  refreshEmptyState(form);
  form.dispatchEvent(new Event('change', { bubbles: true }));
  row.querySelector<HTMLInputElement>('input[data-field="name"]')?.focus();
}

function refreshEmptyState(form: HTMLFormElement): void {
  const note = $<HTMLElement>('[data-empty="item"]', form);
  const list = $<HTMLElement>('[data-list="item"]', form);
  if (note) note.hidden = (list?.querySelectorAll('[data-rule-row]').length ?? 0) > 0;
}

function rows(form: HTMLFormElement): HTMLElement[] {
  return $$<HTMLElement>('[data-list="item"] [data-rule-row]', form);
}

/* ---------------------------------------------------------------- the fields */

function field(row: ParentNode, name: string): Element | null {
  return row.querySelector(`[data-field="${CSS.escape(name)}"]`);
}

function text(row: ParentNode, name: string): string {
  const node = field(row, name);
  return node instanceof HTMLInputElement ? node.value.trim() : '';
}

function checked(row: ParentNode, name: string): boolean {
  const node = field(row, name);
  return node instanceof HTMLInputElement ? node.checked : false;
}

function number(form: HTMLFormElement, name: string, fallback: number): number {
  const value = Number(readText(form, name));
  return Number.isFinite(value) ? value : fallback;
}

/** Reads `7d`, `1m`, `30s` into seconds — the repository's duration grammar. */
function seconds(input: string): number {
  const units: Record<string, number> = { s: 1, m: 60, h: 3600, d: 86_400, w: 604_800 };
  let total = 0;

  for (const match of input.toLowerCase().matchAll(/(\d+)\s*([smhdw])/g)) {
    total += Number(match[1]) * (units[match[2] ?? ''] ?? 0);
  }

  if (total === 0 && /^\d+$/.test(input)) total = Number(input) * 60;
  return total;
}
